import { getWebchatConversations, markRead } from '@/services/webchat-service/webchat.service';
import {
  CONVERSATION_TYPES,
  ConversationType,
  WebchatAgentEvent,
  WebchatConversation,
  isAiCallSession,
} from '@/services/webchat-service/webchat.service.types';
import { BridgeLogEntry, useWebchatStore } from '@/store/webchat.store';
import { useScreenShareStore, decodeBase64Chunk } from '@/store/screen-share.store';
import { notification } from 'antd';
import { t } from 'i18next';
import { useCallback, useEffect, useRef } from 'react';
import { send, subscribe } from '@/services/websocket/ws-client';

// CallsChannel payload shape — the legacy mechanism that powers the
// Conversations tab. The Command Center subscribes alongside the
// WebchatAgentChannel so any Conversation save (broadcast_update) is
// reflected without depending solely on the explicit
// broadcast_to_agent_dashboard call sites in CRM.
type CallsChannelCallPayload = {
  id: number;
  type: ConversationType | string;
  sid: string | null;
  call_sid: string | null;
  phone: string | null;
  language: string | null;
  end_reason: string | null;
  duration: number | null;
  notes: string | null;
  convo_summary: string | null;
  company_id: number;
  company_name: string | null;
  user_id: number | null;
  created_at: string;
  updated_at: string;
};
type CallsChannelEvent = { call: CallsChannelCallPayload };

// True when the conversation has clearly ended. CallsChannel doesn't
// carry webchat_status / end_time for ElevenLabsCall + AgentCall, so we
// only assert "closed" for the legacy types where end_reason is the
// authoritative live signal — the existing session_closed event on
// WebchatAgentChannel handles the AI/agent types.
function isLegacyClosed(call: CallsChannelCallPayload): boolean {
  if (call.type === 'AnalyticsCall') {
    return !!call.end_reason;
  }
  if (call.type === 'TextConversation') {
    return !!call.end_reason && call.end_reason !== 'agent';
  }
  return false;
}

type Announcement = { message: string; description: string };

// Toast text for a freshly created voice / SMS / handoff session. Built at call time so it
// follows the current language; undefined for types that announce elsewhere.
const announcementForCreated = (type: string): Announcement | undefined => {
  switch (type) {
    case 'AnalyticsCall':
      return {
        message: t('webchat:notifications.voiceCall.title'),
        description: t('webchat:notifications.voiceCall.description'),
      };
    case 'TextConversation':
      return {
        message: t('webchat:notifications.sms.title'),
        description: t('webchat:notifications.sms.description'),
      };
    case 'AgentCall':
      return {
        message: t('webchat:notifications.handoff.title'),
        description: t('webchat:notifications.handoff.description'),
      };
    default:
      return undefined;
  }
};

// Queue-arrival toast per channel; anything that is not voice or AI reads as a chat.
const announcementForQueued = (type: string): Announcement => {
  switch (type) {
    case 'AnalyticsCall':
      return {
        message: t('webchat:notifications.queuedVoiceCall.title'),
        description: t('webchat:notifications.queuedVoiceCall.description'),
      };
    case 'ElevenLabsCall':
      return {
        message: t('webchat:notifications.queuedAiCall.title'),
        description: t('webchat:notifications.queuedAiCall.description'),
      };
    default:
      return {
        message: t('webchat:notifications.queuedChat.title'),
        description: t('webchat:notifications.queuedChat.description'),
      };
  }
};

const NOTIFICATION_SOUND_BASE64 =
  'data:audio/wav;base64,UklGRl4GAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YToGAACAgICAgICAgICBgYGBgoKCgoODg4OEhISEhYWFhYaGhoaHh4eHiIiIiImJiYmKioqKi4uLi4yMjIyNjY2Njo6Ojo+Pj4+QkJCQkZGRkZKSkpKTk5OTlJSUlJWVlZWWlpaWl5eXl5iYmJiZmZmZmpqampubm5ucnJycnZ2dnZ6enp6fn5+foKCgoKGhoaGioqKio6Ojo6SkpKSlpaWlpqampqenp6eoqKioqampqaqqqqqqq6urq6ysrKytra2trq6urq+vr6+wsLCwsbGxsbKysrKzs7Ozt7e3t7i4uLi0tLS0sbGxsa6urq6rq6urqKioqKWlpaWioqKin5+fn5ycnJyZmZmZlpaWlpOTk5OQkJCQjY2NjYqKioqHh4eHhISEhIGBgYF+fn5+e3t7e3h4eHh1dXV1cnJycm9vb29sbGxsaWlpaWZmZmZjY2NjYGBgYF1dXV1aWlpaV1dXV1RUVFRPT09PTk5OTlFRUVFUVFRUV1dXV1paWlpdXV1dYGBgYGNjY2NmZmZmaWlpaWxsbGxvb29vcnJycnV1dXV4eHh4e3t7e35+fn6BgYGBhISEhIeHh4eKioqKjY2NjZCQkJCTk5OTlpaWlpmZmZmcnJycn5+fn6KioqKlpaWlqKioqKurq6uurq6usbGxsbS0tLS3t7e3s7OzsrCwsLCtra2tqqqqqaenp6ekpKSkoaGhoZ6enp6bm5ubmJiYmJWVlZWSkpKSj4+Pj4yMjIyJiYmJhoaGhoODg4OAgICAgICAgICAgICAgICAgA==';

const playNotificationSound = () => {
  try {
    const audio = new Audio(NOTIFICATION_SOUND_BASE64);
    audio.volume = 0.3;
    audio.play().catch(() => {
      // Browser may block autoplay
    });
  } catch {
    // Ignore audio errors
  }
};

export const useWebchatAgent = () => {
  const typingTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const {
    setSessions,
    upsertSession,
    removeSession,
    addMessage,
    appendLiveTranscriptTurn,
    setVisitorTyping,
    setDisconnected,
    setBridgeStatus,
    setBridgeAgentLanguage,
    setBridgeWarning,
    setTranslateEnabled,
    addBridgeLogEntry,
    updateBridgeLogDuration,
  } = useWebchatStore();

  // Tracks the current in-progress step per (conversationId, role) so we can
  // compute durations when the next status event arrives.
  // Key: `${conversationId}:${role}`, value: { entryId, startedAt }
  const bridgeLogStepRef = useRef<Record<string, { id: string; startedAt: number }>>({});

  const handleEvent = useCallback(
    (data: WebchatAgentEvent) => {
      const { soundEnabled, selectedConversationId } = useWebchatStore.getState();

      switch (data.type) {
        case 'active_sessions':
          setSessions(data.sessions);
          break;

        case 'session_created': {
          upsertSession(data.conversation);
          // Voice + SMS lifecycles fire session_created (AnalyticsCall +
          // TextConversation from UserPrepareService, AgentCall on handoff).
          // Webchat uses session_queued and AI calls use session_started, so
          // those branches own their own notification + sound — don't double up.
          const announcement = announcementForCreated(data.conversation?.type ?? '');
          if (announcement) {
            notification.info({
              ...announcement,
              placement: 'topRight',
              duration: 5,
            });
            if (soundEnabled && document.hidden) {
              playNotificationSound();
            }
          }
          break;
        }

        case 'session_started': {
          upsertSession(data.conversation);
          // Currently the CRM only emits session_started for AI calls (the
          // initiation webhook handler), but we keep the branch general so
          // future non-AI session-started flows still get a sensible label.
          const isAiCall = isAiCallSession(data.conversation);
          notification.info({
            message: isAiCall ? t('webchat:notifications.aiCall.title') : t('webchat:notifications.session.title'),
            description: isAiCall
              ? t('webchat:notifications.aiCall.description')
              : t('webchat:notifications.session.description'),
            placement: 'topRight',
            duration: 5,
          });
          if (soundEnabled && document.hidden) {
            playNotificationSound();
          }
          break;
        }

        case 'session_queued': {
          upsertSession(data.conversation);
          // Differentiate the queue-arrival announcement by channel so
          // the rep can triage at a glance from the toast alone. Voice
          // calls (AnalyticsCall) need the on-hold framing — they have
          // hold music ticking; chat sessions don't.
          const queuedAnnouncement = announcementForQueued(data.conversation?.type ?? '');
          notification.info({
            ...queuedAnnouncement,
            placement: 'topRight',
            duration: 5,
          });
          if (soundEnabled && document.hidden) {
            playNotificationSound();
          }
          break;
        }

        case 'live_transcript':
          appendLiveTranscriptTurn(data.conversation_id, data.turn);
          break;

        case 'language_detected': {
          const langSession = useWebchatStore
            .getState()
            .sessions.find((s) => s.id === data.conversation_id);
          if (langSession) {
            upsertSession({ ...langSession, language: data.language });
          }
          break;
        }

        case 'session_assigned':
        case 'session_transferred': {
          // When the claim includes a detected visitor_language that differs from
          // the serialized conversation.language (which defaults to 'en-us'), use
          // the visitor_language so the BridgeStatusBar shows the correct caller
          // language immediately on claim without waiting for a bridge turn.
          const conversationWithLang =
            data.visitor_language && data.visitor_language !== data.conversation.language
              ? { ...data.conversation, language: data.visitor_language }
              : data.conversation;
          upsertSession(conversationWithLang);
          if (data.agent_language) {
            setBridgeAgentLanguage(data.conversation_id, data.agent_language);
          }
          if (data.error) {
            notification.error({
              message: t('webchat:notifications.transferFailed.title'),
              description:
                typeof data.error === 'string'
                  ? data.error
                  : t('webchat:notifications.transferFailed.description'),
              placement: 'topRight',
              duration: 6,
            });
          }
          break;
        }

        case 'bridge_status': {
          setBridgeStatus(data.conversation_id, data.role, data.status);
          const now = Date.now();
          const stepKey = `${data.conversation_id}:${data.role}`;
          const prevStep = bridgeLogStepRef.current[stepKey];
          // Close out the previous step's duration
          if (prevStep) {
            updateBridgeLogDuration(data.conversation_id, prevStep.id, now - prevStep.startedAt);
            delete bridgeLogStepRef.current[stepKey];
          }
          // Record a new step for every non-idle status
          if (data.status !== 'idle') {
            const entry: BridgeLogEntry = {
              id: `${now}-${Math.random().toString(36).slice(2)}`,
              role: data.role as 'visitor' | 'agent',
              status: data.status,
              startedAt: now,
              durationMs: null,
            };
            addBridgeLogEntry(data.conversation_id, entry);
            bridgeLogStepRef.current[stepKey] = { id: entry.id, startedAt: now };
          }
          break;
        }

        case 'bridge_warning':
          // Latest warning per conversation wins; cleared on session close.
          // The actions sidebar reads this and renders a yellow tag so the
          // rep knows the configured voice is broken without LLM-log access.
          setBridgeWarning(data.conversation_id, {
            kind: data.kind,
            role: data.role,
            details: data.details,
          });
          break;

        case 'session_closed':
          removeSession(data.conversation_id);
          useWebchatStore.getState().clearBridgeWarning(data.conversation_id);
          // Clean up any pending step timers for this conversation
          Object.keys(bridgeLogStepRef.current).forEach((key) => {
            if (key.startsWith(`${data.conversation_id}:`)) {
              delete bridgeLogStepRef.current[key];
            }
          });
          break;

        case 'new_message': {
          addMessage(data.conversation_id, data.message);
          // Update session's last_message if session exists
          const existingSession = useWebchatStore
            .getState()
            .sessions.find((s) => s.id === data.conversation_id);
          if (existingSession) {
            upsertSession({
              ...existingSession,
              last_message: data.message,
              unread_count:
                data.conversation_id === selectedConversationId
                  ? existingSession.unread_count
                  : existingSession.unread_count + (data.message.sender_type === 'visitor' ? 1 : 0),
            });
          }

          // Auto mark-read if this conversation is currently selected
          if (data.conversation_id === selectedConversationId) {
            markRead(data.conversation_id).catch(() => {});
          } else if (data.message.sender_type === 'visitor') {
            // Notify for messages in non-selected conversations
            const preview = data.message.translated_text || data.message.original_text;
            notification.info({
              message: t('webchat:notifications.newMessage'),
              description: preview.length > 80 ? `${preview.slice(0, 80)}...` : preview,
              placement: 'topRight',
              duration: 4,
            });
            if (soundEnabled && document.hidden) {
              playNotificationSound();
            }
          }
          break;
        }

        case 'visitor_typing': {
          const convId = data.conversation_id;
          setVisitorTyping(convId, true);
          // Clear previous timer
          if (typingTimers.current[convId]) {
            clearTimeout(typingTimers.current[convId]);
          }
          typingTimers.current[convId] = setTimeout(() => {
            setVisitorTyping(convId, false);
          }, 3000);
          break;
        }

        case 'visitor_disconnected':
          setDisconnected(data.conversation_id, true);
          upsertSession(data.conversation);
          break;

        case 'visitor_reconnected':
          setDisconnected(data.conversation_id, false);
          upsertSession(data.conversation);
          break;

        case 'idle_warning': {
          const session = useWebchatStore
            .getState()
            .sessions.find((s) => s.id === data.conversation_id);
          if (session) {
            upsertSession({ ...session, idle_since: data.idle_since });
          }
          break;
        }

        case 'agent_availability_changed':
          // Handled by refetching available_agents via react-query invalidation
          break;

        case 'translate_toggled':
          setTranslateEnabled(data.translate);
          break;

        case 'screen_started':
          useScreenShareStore.getState().start(data.conversation_id, data.mime);
          break;

        case 'screen_chunk':
          useScreenShareStore
            .getState()
            .pushChunk(data.conversation_id, decodeBase64Chunk(data.chunk));
          break;

        case 'screen_ended':
          useScreenShareStore.getState().end(data.conversation_id);
          break;
      }
    },
    [
      setSessions,
      upsertSession,
      removeSession,
      addMessage,
      appendLiveTranscriptTurn,
      setVisitorTyping,
      setDisconnected,
      setBridgeStatus,
      setBridgeAgentLanguage,
      setBridgeWarning,
      setTranslateEnabled,
      addBridgeLogEntry,
      updateBridgeLogDuration,
    ],
  );

  const handleEventRef = useRef(handleEvent);
  useEffect(() => {
    handleEventRef.current = handleEvent;
  }, [handleEvent]);

  // CallsChannel handler — legacy broadcast_update stream that carries
  // every Conversation save. We use it to keep the Command Center in
  // sync for rai-routed calls (AnalyticsCall, TextConversation, AgentCall)
  // whose lifecycle is driven by broadcast_update rather than the explicit
  // broadcast_to_agent_dashboard call sites the EL flow uses. Merges into
  // existing sessions so the richer WebchatConversationSerializer payload
  // from session_created isn't downgraded by ConversationSerializer's
  // narrower shape.
  const handleCallsChannelEvent = useCallback(
    async (data: CallsChannelEvent) => {
      const call = data?.call;
      if (!call?.id) return;
      // Only the conversation types the Command Center surfaces; ignore
      // post-call analytics rows that broadcast for other dashboards.
      if (!(CONVERSATION_TYPES as readonly string[]).includes(call.type)) return;

      const { sessions } = useWebchatStore.getState();
      const existing = sessions.find((s) => s.id === call.id);

      if (isLegacyClosed(call)) {
        if (existing) removeSession(call.id);
        return;
      }

      if (existing) {
        // Merge — preserve fields CallsChannel doesn't carry
        // (assigned_agent_id, webchat_status, idle_since, last_message,
        // live_transcript_turns, returning_caller, …).
        
        upsertSession({
          ...existing,
          language: call.language ?? existing.language,
          phone: call.phone ?? existing.phone,
          notes: call.notes ?? existing.notes,
          convo_summary: call.convo_summary ?? existing.convo_summary,
          duration: call.duration ?? existing.duration,
          updated_at: call.updated_at,
        });
        return;
      }

      // New session we haven't seen via WebchatAgentChannel yet — fetch
      // the full WebchatConversation shape so the card has every field
      // its renderer expects. Refetch the active list rather than build a
      // synthetic record; one HTTP call beats N field defaults that drift.
      try {
        const fresh = await getWebchatConversations();
        const match = fresh.find((s: WebchatConversation) => s.id === call.id);
        if (match) upsertSession(match);
      } catch {
        // Network blip — next save broadcast or page load will recover.
      }
    },
    [upsertSession, removeSession]
  );

  const handleCallsChannelEventRef = useRef(handleCallsChannelEvent);
  useEffect(() => {
    handleCallsChannelEventRef.current = handleCallsChannelEvent;
  }, [handleCallsChannelEvent]);

  useEffect(() => {
    // ws-client reference-counts per channel, so this hook and use-call-log share one
    // CallsChannel socket.
    const unsubscribeAgent = subscribe('WebchatAgentChannel', (data) => {
      handleEventRef.current(data as WebchatAgentEvent);
    });

    const unsubscribeCalls = subscribe('CallsChannel', (data) => {
      handleCallsChannelEventRef.current(data as CallsChannelEvent);
    });

    const timers = typingTimers.current;

    return () => {
      unsubscribeAgent();
      unsubscribeCalls();
      // Clear all typing timers
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  // `action` is the discriminator the server switches on. Inert until the Command Center
  // channel ships: no socket opens, and the frames would be swallowed if it did.
  const sendTyping = useCallback((conversationId: number) => {
    send('WebchatAgentChannel', { action: 'typing', conversation_id: conversationId });
  }, []);

  const sendToggleTranslate = useCallback((conversationId: number, translate: boolean) => {
    send('WebchatAgentChannel', {
      action: 'toggle_translate',
      conversation_id: conversationId,
      translate,
    });
  }, []);

  const sendSetAgentLanguage = useCallback((conversationId: number, agentLanguage: string) => {
    send('WebchatAgentChannel', {
      action: 'set_agent_language',
      conversation_id: conversationId,
      agent_language: agentLanguage,
    });
  }, []);

  return { sendTyping, sendToggleTranslate, sendSetAgentLanguage };
};
